<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import ModalManager from "@/components/modal/ModalManager";
import NzSyncContractEditor from "@/components/editors/NzSyncContractEditor.vue";
import ActivityManager from "@/services/ActivityManager";

/* ============================================================
   State
============================================================ */
const shops = ref([]);
const loading = ref(false);
const runningShopDomain = ref(null);

const error = ref("");
const status = ref("");
const progressByCapability = ref({});
const runningShop = ref(null);

const newShopDomain = ref("");

let eventSource = null;
const ENTITY_CAPABILITIES = new Set([
  "core",
  "identities",
  "content",
  "price",
  "variants",
  "stock",
  "bricks"
]);

/* ============================================================
   Helpers
============================================================ */
function apiHeaders() {
  return {
    "Content-Type": "application/json",
    "x-api-key": localStorage.getItem("api_key"),
  };
}

function getEntityIds(cap, capData) {

  if (!ENTITY_CAPABILITIES.has(cap)) return [];
  if (!Array.isArray(capData.events)) return [];

  const ids = [];

  for (const e of capData.events) {
    if (Array.isArray(e.entity_ids)) {
      ids.push(...e.entity_ids);
    } else if (e.entity_id != null) {
      ids.push(e.entity_id);
    }
  }

  return [...new Set(ids)];

}

/* ============================================================
   Modal: Edit Sync Contract
============================================================ */
function openEditModal(shop) {
  ModalManager.open({
    title: "ComfortPages Webshop Sync Contract",
    size: "md",

    record: {
      id: shop.id,
      label: shop.shop_domain,
    },

    component: NzSyncContractEditor,
    props: {
      record: shop,
    },

    onClose(payload) {
      if (!payload?.ok || !payload.shop) return;

      const idx = shops.value.findIndex(
        (s) => s.id === payload.shop.id
      );

      if (idx !== -1) {
        // 🔁 full replace (important for shop_domain changes)
        shops.value[idx] = payload.shop;
      }
    },
  });
}

/* ============================================================
   Load subscribed shops
============================================================ */
async function loadShops() {
  loading.value = true;
  error.value = "";

  try {
    const res = await fetch("/api/shops/nz", {
      headers: apiHeaders(),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || "Failed to load shops");
    }

    shops.value = json.shops || [];
  } catch (err) {
    error.value = err.message;
  } finally {
    loading.value = false;
  }
}

/* ============================================================
   Add shop
============================================================ */
async function addShop() {
  const domain = newShopDomain.value.trim();
  if (!domain) return;

  error.value = "";

  try {
    const res = await fetch("/api/shops/subscribe/nz", {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({
        shop_domain: domain,
      }),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || "Failed to add shop");
    }

    const shop = json.shop;

    const idx = shops.value.findIndex((s) => s.id === shop.id);
    if (idx !== -1) {
      shops.value[idx] = shop;
    } else {
      shops.value.push(shop);
    }

    shops.value.sort((a, b) =>
      a.shop_domain.localeCompare(b.shop_domain)
    );

    newShopDomain.value = "";
  } catch (err) {
    error.value = err.message;
  }
}

/* ============================================================
   Progress listener (SSE)
============================================================ */
function startProgressListener() {
  progressByCapability.value = {};

  const apiKey = localStorage.getItem("api_key");
  if (!apiKey) {
    error.value = "Missing API key";
    return;
  }

  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }

  eventSource = new EventSource(
    `/api/shops/sync/nz/progress?apiKey=${encodeURIComponent(apiKey)}`
  );

  eventSource.addEventListener("progress", (e) => {
    if (!eventSource || !e?.data) return;

    try {
      handleProgressMessage(JSON.parse(e.data));
    } catch {
      /* ignore */
    }
  });

  eventSource.onerror = () => {};
}

function cleanupAfterRun() {
  runningShopDomain.value = null;
  ActivityManager.stop("manual-sync");
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }

}

function resetMessages() {
  error.value = "";
  status.value = "";
  progressByCapability.value = {};
}

/* ============================================================
   Start manual sync
============================================================ */
async function startSync(shop) {
  if (runningShopDomain.value) return;

  resetMessages();
  runningShopDomain.value = shop.shop_domain;
  startProgressListener();
  ActivityManager.start("manual-sync");

  try {
    const res = await fetch("/api/shops/sync/nz/start", {
      method: "POST",
      headers: apiHeaders(),
      body: JSON.stringify({
        shop_domain: shop.shop_domain,
        triggeredBy: "manual",
        customerId: localStorage.getItem('customer_id')
      }),
    });

    const json = await res.json();
    if (!res.ok) {
      throw new Error(json.error || "Failed to start sync");
    }

    status.value = `Sync started for ${shop.shop_domain}…`;
    ActivityManager.stop("manual-sync");

  } catch (err) {
    error.value = err.message;
    cleanupAfterRun();

  }
}

/* ============================================================
   Progress handling
============================================================ */
function handleProgressMessage(msg) {
  // --- run-level ---
  if (msg.type === "run:error") {
    error.value = msg.message || "Sync failed";
    cleanupAfterRun();
    return;
  }

  if (msg.type === "run:done") {
    status.value = `Sync finished in ${msg.totalSeconds}s`;
    cleanupAfterRun();
    return;
  }

  // --- capability-scoped ---
  if (!msg.capability) return;

  const cap = msg.capability;

  if (!progressByCapability.value[cap]) {
    progressByCapability.value[cap] = {
      status: "running",
      events: [],
    };
  }

  // ✅ STORE *ALL* capability events
  progressByCapability.value[cap].events.push(msg);

  // --- status transitions ---
  if (msg.type === "capability:start") {
    progressByCapability.value[cap].status = "running";
  }

  if (msg.type === "capability:done") {
    progressByCapability.value[cap].status = "done";
  }
}

/* ============================================================
   Lifecycle
============================================================ */
onMounted(loadShops);

onBeforeUnmount(() => {
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
});
</script>

<template>
  <section class="cp-wrap">
    <h1 class="tc">
      Node.Zone <span class="icon-arrow-right"></span>
      ComfortPages <span class="icon-cloud-sync"></span>
      Synchronisation
    </h1>

    <div class="nz-nested">
      <div class="nz-grid-hh nz-nested-grid">

        <!-- LEFT -->
        <div class="nz-grid-item-hh nz-syn-left">
          <div class="nz-nested-1">
            <h2 class="nz-tmp-info"><span class="icon-cloud-sync"></span> Die Synchronisation läuft jetzt automatisch – alle 15 Minuten!</h2>
            <h3>Subscribed Shops</h3>
            <div v-if="loading">Loading shops…</div>

            <ul v-else class="progress shops">
              <li v-for="shop in shops" :key="shop.id">
                <span
                    class="icon-cloud"
                    :class="{ 'icon-cloud-cross': !shop.active }"
                  ></span>
                <span class="li-text">{{ shop.shop_domain }}</span>

                <button
                  class="btn btn-danger btn-icon-text btn-sm"
                  @click="startSync(shop)"
                  :disabled="runningShopDomain || !shop.active"
                >
                  <span class="icon-cloud-sync"></span>
                  {{
                    runningShopDomain === shop.shop_domain
                      ? "Syncing…"
                      : "Start sync"
                  }}
                </button>

                <button
                  class="btn btn-icon-flat"
                  @click="openEditModal(shop)"
                >
                  <span class="icon-cog"></span>
                </button>
              </li>
            </ul>

            <!-- ADD SHOP -->
            <div class="add-wrap">
              <input id="new_domain" type="text"
                v-model="newShopDomain"
                placeholder="new domain"
              />
              <button class="btn btn-icon" @click="addShop">
                <span class="icon-plus"></span>
              </button>
            </div>
          </div>
        </div>

        <!-- RIGHT -->
        <div class="nz-grid-item-hh">
          <div class="nz-nested-2 nz-info">

            <div v-if="!status && !error" class="success">
              <h3>
                <span class="icon-cloud-sync" style="font-size: 5rem;"></span>
                Manual Sync – Node.Zone → ComfortPages
              </h3>
              Trigger a manual sync for all enabled capabilities for a shop
            </div>

            <div
              class="tc"
              v-if="Object.keys(progressByCapability).length === 0 && runningShopDomain"
            >
              <em>Waiting for progress…</em>
            </div>

            <div class="nz-info-inner">
              <div v-if="Object.keys(progressByCapability).length">
                <div
                  v-for="(capData, cap) in progressByCapability"
                  :key="cap"
                  class="nz-capability-block"
                >
                  <h4 class="nz-capability-header">
                    <span class="icon-cog"></span>
                    {{ cap }}

                    <span
                      v-if="capData.status === 'done'"
                      class="icon-check nz-status-done"
                    ></span>
                  </h4>

                  <ul class="progress">
                    <li v-for="(p, i) in capData.events" :key="i">
                      {{ p.type }}
                    </li>
                    <!-- Entity IDs (articles only) -->
                      <!-- Entity IDs (articles only) -->
                      <li
                        v-if="getEntityIds(cap, capData).length"
                        class="synced-articles"
                      >
                        <span
                          v-for="id in getEntityIds(cap, capData)"
                          :key="id"
                          class="article-id"
                        >
                          {{ id }}
                        </span>
                      </li>
                  </ul>

                </div>
              </div>

              <p v-if="status" class="success">{{ status }}</p>
              <p v-if="error" class="error">{{ error }}</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style lang="less" scoped>

// h2
.nz-tmp-info {
  display: flex;
  margin: 0; // remove default spacing
  align-items: center;   // vertically centers children
  padding: 16px 0px;
  border-radius: 6px;
  border: 1px solid @gray;

  span {
    padding: 0px 26px 0px 26px;
    font-size: 3rem;
  }

}

h1{
  span{
    display: inline-block;
    transform: translateY(3px);
    padding: 0px 12px;

  }
}

div.nz-grid-hh{
  max-width: 1400px;
  margin: 0 auto;

}

div.nz-syn-left{
  padding: 0px !important;

}
label {
  display: block;
  font-weight: 600;
}

div.nz-nested {
  text-align: center;
}

ul.progress {
  width: 600px;
  display: inline-block;
  margin: 0 auto;
  list-style: none;
  li {
    text-align: left;
    line-height: 2rem;
    padding: 6px;
    span:not(.article-id) {
      font-size: 1.5rem;
      color: @accent;
      display: inline-block;
      margin: 3px 10px 3px 0px;
    }
    span.li-text{
      transform: translateY(-3px);
      font-size: 15px;
      color: @text;

    }
    span.icon-cloud{
      font-size: 2rem;
      transform: translateY(4px);
      margin-left: 10px;

    }

  }
  button{
    transform: translateY(2px);
    min-width: 150px; /* such that it stays the same with the synching ... text */

  }
  button.btn-icon-flat{
    min-width: auto;
  }
  button:disabled{
    pointer-events: none !important;
    opacity: .6 !important;
  }
}

ul.shops{
  border-radius: 6px;
  border: 1px solid @gray;
  li{
    background: #F0F0F0;
    background: linear-gradient(0deg, rgba(240, 240, 240, 1) 0%, rgba(255, 255, 255, 0) 64%);

  }

}

.btn-icon-text{
  float: right;
  span{
    color: #fff !important;
  }

}

.btn-icon-flat{
  float: right;

}
.icon-cloud-cross{
  color: @gray-darker !important;
}
.nz-info{
  width: 100%;
  min-height: 60vh;
  max-height: 60vh;
  position: relative;
  overflow: hidden;

}

.nz-info h3 {
  color: @accent;

  span {
    font-size: 3rem;
    display: block;
    margin: 0 auto 12px auto;
  }
}

.nz-info-inner{
  position: absolute;
  width: 100%;
  padding: 0px 12px 20px 0px;
  bottom: 0px;

}

.nz-cp-i-done {
  color: @accent;
}

.nz-grid-item-hh {
  text-align: center;
  min-height: 700px;

}

.actions {
  width: 100%;
  text-align: center;

  button {
    margin: 20px;
  }
}

.success {
  color: @accent;
  margin-top: 1rem;
}

.error {
  color: @danger;
  margin-top: 1rem;
}

.nz-capability-header {
  display: flex;
  float: left;
  width: 116px;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  padding: 0;
}

.nz-status-error {
  color: @danger;
  font-weight: bold;
}

.nz-status-done {
  color: @accent;
}

.add-wrap{
  text-align: right;
  padding: 20px 20px 0px 0px;
  button{
    transform: translateY(-10px);
  }
}
#new_domain{
  margin: 16px 16px 0px 0px;
  display: inline-block;

}

li.synced-articles{
  position: relative;
  padding: 0px 0px 0px 25px !important;
  &:after{
    position: absolute;
    top: 0px;
    left: -16px;
    font-family: 'icomoon' !important;
    speak: none;
    font-style: normal;
    font-weight: normal;
    font-variant: normal;
    text-transform: none;
    font-size: 1.4rem;
    color: @accent;
    display: inline;
    margin: 0px 10px 0px 15px;
    /* Better Font Rendering =========== */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    content: "\e755";

  }

}
span.article-id{
  display: inline-block;
  font-size: 1rem;
  color: @text;
  padding: 0px 4px;

}
</style>
